import { createFileRoute } from "@tanstack/react-router";
export const Route = createFileRoute("/user/_layout/crm/emails")({
  component: EmailsPage,
});

import { useState, useEffect, useMemo } from "react";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { EmailTable } from "@/components/crm/tables/EmailTable";
import {
  useDataTableOrdering,
  type SortDirection,
} from "@/hooks/ui/useDataTableOrdering";
import {
  EmailFormSheet,
  EMAIL_STATUSES,
  EMAIL_DIRECTIONS,
} from "@/components/crm/forms/EmailFormSheet";
import {
  useFetchEmails,
  useEmailMutations,
  useMarkEmailSent,
  type EmailOrdering,
} from "@/hooks/api/crm/useEmails";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { AssigneeFilter } from "@/components/crm/AssigneeFilter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import type { CrmEmail, CrmEmailPayload } from "@/types/crm";

function EmailsPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebouncedSearch] = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState("");
  const [directionFilter, setDirectionFilter] = useState("");
  const [assignedTo, setAssignedTo] = useState<number | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);

  const { ordering, toggleOrdering, getApiOrdering, setOrdering } =
    useDataTableOrdering<
      {
        subject: SortDirection;
        status: SortDirection;
        direction: SortDirection;
        email_datetime: SortDirection;
        assigned_to__first_name: SortDirection;
      },
      EmailOrdering
    >({
      subject: null,
      status: null,
      direction: null,
      email_datetime: null,
      assigned_to__first_name: null,
    });

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<CrmEmail | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<CrmEmail | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const emailsQuery = useFetchEmails({
    page,
    page_size: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    direction: directionFilter || undefined,
    assigned_to: assignedTo ? String(assignedTo) : undefined,
    ordering: getApiOrdering(),
  });
  const emails = emailsQuery.data?.results ?? [];
  const count = emailsQuery.data?.count ?? 0;

  const { createEmail, updateEmail, deleteEmail } = useEmailMutations();
  const markEmailSent = useMarkEmailSent();

  const isMutating =
    createEmail.isPending ||
    updateEmail.isPending ||
    deleteEmail.isPending ||
    markEmailSent.isPending;
  const isLoading = emailsQuery.isPending || isMutating;
  const isFetching = emailsQuery.isFetching || isMutating;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(count / pageSize)),
    [count, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, directionFilter, assignedTo, ordering, setPage]);

  const resetFilters = () => {
    setSearchQuery("");
    resetDebouncedSearch();
    setStatusFilter("");
    setDirectionFilter("");
    setAssignedTo(undefined);
    setOrdering({
      subject: null,
      status: null,
      direction: null,
      email_datetime: null,
      assigned_to__first_name: null,
    });
    setPage(1);
  };

  const handlePageChange = (p: number) => {
    if (p >= 1 && p <= totalPages) {
      setPage(p);
    }
  };

  const onCreate = async (data: CrmEmailPayload) => {
    await createEmail.mutateAsync(data);
    toast.success("Email logged successfully!");
    setIsSheetOpen(false);
  };
  const onEdit = async (id: string, data: Partial<CrmEmailPayload>) => {
    await updateEmail.mutateAsync({ id, payload: data });
    toast.success("Email updated successfully!");
    setIsSheetOpen(false);
  };
  const handleMarkSent = async (email: CrmEmail) => {
    try {
      await markEmailSent.mutateAsync({ id: email.id });
      toast.success("Email marked as sent!");
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };
  const handleDeleteConfirm = async () => {
    if (!emailToDelete) return;
    try {
      await deleteEmail.mutateAsync({ id: emailToDelete.id });
      toast.success("Email deleted successfully!");
      setIsDeleteDialogOpen(false);
      setEmailToDelete(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Emails"
          description="Log and manage emails."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => {
            setEditingEmail(null);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          addLabel="Log Email"
          isLoading={isFetching}
        />
        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search emails..."
          />
          <AssigneeFilter value={assignedTo} onChange={setAssignedTo} className="w-[220px]" />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {EMAIL_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={directionFilter}
            onValueChange={(v) => setDirectionFilter(v === "all" ? "" : v)}
            disabled={isFetching}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Directions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Directions</SelectItem>
              {EMAIL_DIRECTIONS.map((d) => (
                <SelectItem key={d} value={d}>
                  {d}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FiltersPanel>
        <EmailTable
          emails={emails}
          onView={(e) => {
            setEditingEmail(e);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(e) => {
            setEditingEmail(e);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(e) => {
            setEmailToDelete(e);
            setIsDeleteDialogOpen(true);
          }}
          onMarkSent={handleMarkSent}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={toggleOrdering}
        />
        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
        <EmailFormSheet
          isOpen={isSheetOpen}
          isViewOnly={isViewOnly}
          onEditClick={isViewOnly ? () => setIsViewOnly(false) : undefined}
          onClose={() => setIsSheetOpen(false)}
          onCreate={onCreate}
          onEdit={onEdit}
          editingEmail={editingEmail}
          isLoading={isMutating}
        />
        <DeleteConfirmDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Email"
          description={`Delete email "${emailToDelete?.subject}"?`}
          itemName={emailToDelete?.subject}
          isLoading={deleteEmail.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
